import { DEFAULT_THEME_MODE, ThemeMode, isThemeMode } from './guideTokens';
import { DEFAULT_BLUE_LIGHT_LEVEL, clampBlueLightLevel } from './blueLight';

/**
 * Preferencia de tema por usuario. Se guarda en localStorage con una clave por usuario logueado
 * (el `sub` del perfil OIDC). Si el storage no está disponible (modo privado, cuota, políticas),
 * se recuerda en memoria durante la sesión. Sin preferencia guardada: el claro de la guía.
 */
const KEY_PREFIX = 'ichtys.theme';
const ANONYMOUS = 'anonymous';

const memoryStore = new Map<string, ThemeMode>();

/**
 * Reducción de luz azul (filtro cálido tipo "luz nocturna"). Es independiente del modo: se puede
 * combinar con claro, oscuro o alto contraste. Misma política de guardado que el modo.
 */
const BLUE_LIGHT_PREFIX = 'ichtys.bluelight';
const blueLightMemoryStore = new Map<string, boolean>();
const BLUE_LIGHT_LEVEL_PREFIX = 'ichtys.bluelight.level';
const blueLightLevelMemoryStore = new Map<string, number>();

export const themeStorageKey = (userId?: string | null): string =>
  `${KEY_PREFIX}.${userId && String(userId).trim() ? String(userId).trim() : ANONYMOUS}`;

export const readThemePreference = (userId?: string | null): ThemeMode => {
  const key = themeStorageKey(userId);
  try {
    const stored = window.localStorage.getItem(key);
    if (isThemeMode(stored)) return stored;
  } catch {
    // storage inaccesible: se usa la copia en memoria
  }
  return memoryStore.get(key) ?? DEFAULT_THEME_MODE;
};

export const writeThemePreference = (userId: string | null | undefined, mode: ThemeMode): void => {
  const key = themeStorageKey(userId);
  memoryStore.set(key, mode);
  try {
    window.localStorage.setItem(key, mode);
  } catch {
    // storage inaccesible: queda la copia en memoria
  }
};

export const blueLightStorageKey = (userId?: string | null): string =>
  `${BLUE_LIGHT_PREFIX}.${userId && String(userId).trim() ? String(userId).trim() : ANONYMOUS}`;

export const readBlueLightPreference = (userId?: string | null): boolean => {
  const key = blueLightStorageKey(userId);
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
  } catch {
    // storage inaccesible: se usa la copia en memoria
  }
  return blueLightMemoryStore.get(key) ?? false;
};

export const writeBlueLightPreference = (userId: string | null | undefined, enabled: boolean): void => {
  const key = blueLightStorageKey(userId);
  blueLightMemoryStore.set(key, enabled);
  try {
    window.localStorage.setItem(key, enabled ? 'on' : 'off');
  } catch {
    // storage inaccesible: queda la copia en memoria
  }
};

export const blueLightLevelStorageKey = (userId?: string | null): string =>
  `${BLUE_LIGHT_LEVEL_PREFIX}.${userId && String(userId).trim() ? String(userId).trim() : ANONYMOUS}`;

/** Intensidad del filtro (porcentaje, 10..100). Sin valor guardado: el nivel medio. */
export const readBlueLightLevel = (userId?: string | null): number => {
  const key = blueLightLevelStorageKey(userId);
  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null && stored.trim() !== '' && Number.isFinite(Number(stored))) return clampBlueLightLevel(stored);
  } catch {
    // storage inaccesible: se usa la copia en memoria
  }
  return blueLightLevelMemoryStore.get(key) ?? DEFAULT_BLUE_LIGHT_LEVEL;
};

export const writeBlueLightLevel = (userId: string | null | undefined, level: number): void => {
  const key = blueLightLevelStorageKey(userId);
  const value = clampBlueLightLevel(level);
  blueLightLevelMemoryStore.set(key, value);
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // storage inaccesible: queda la copia en memoria
  }
};

/** Sólo para tests. */
export const clearThemeMemoryStore = (): void => {
  memoryStore.clear();
  blueLightMemoryStore.clear();
  blueLightLevelMemoryStore.clear();
};
